import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import './LoaderLanding.css';
import { useTranslation } from 'react-i18next';
import hero1 from '../../assets/images/hero/hero_1.jpg';
import hero2 from '../../assets/images/hero/hero_2.jpg';
import hero3 from '../../assets/images/hero/hero_3.jpg';
import hero4 from '../../assets/images/hero/hero_4.jpg';
import hero5 from '../../assets/images/hero/hero_5.jpg';
import garValenLogo from '../../assets/main_assets/Logo/logo.svg';
import arrowIcon from '../../assets/arrow.svg';
import { trackSubmitLetters, trackPageView } from '../../analytics';

const heroImages = [hero1, hero2, hero3, hero4, hero5];

const LoaderLanding = ({ onComplete, onModalShow }) => {
  const { t } = useTranslation();
  const landingRef = useRef(null);
  const leftRef = useRef(null);
  const brandLogoRef = useRef(null);
  const presentsRef = useRef(null);
  const devainRef = useRef(null);
  const rightSectionRef = useRef(null);
  const heroImageRefs = useRef([]);
  const heroImagesContainerRef = useRef(null);
  const modalRef = useRef(null);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const heroCycleIntervalRef = useRef(null);

  useEffect(() => {
    const fireInitialPageView = () => {
      console.log('%c[ANALYTICS] Firing initial landing page view (virtual_page_load)', 'color: #4caf50; font-weight: bold');
      trackPageView();
    };

    if (typeof window._satellite !== 'undefined') {
      fireInitialPageView();
    } else {
      const pollInterval = setInterval(() => {
        if (typeof window._satellite !== 'undefined') {
          clearInterval(pollInterval);
          fireInitialPageView();
        }
      }, 200);
      return () => clearInterval(pollInterval);
    }
  }, []);

  useEffect(() => {
    const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
    
    // Scene 1: Left side animation (keep original)
    tl.fromTo(landingRef.current, { opacity: 0 }, { opacity: 1, duration: 1.5 })
      .fromTo(brandLogoRef.current, { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9 }, '-=0.5')
      .fromTo(presentsRef.current, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.4 }, '-=0.2')
      .fromTo(devainRef.current, { y: 32, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7 }, '+=0.15');

    // Initial state: Set right section and hero images
    gsap.set(rightSectionRef.current, { 
      scale: 1, 
      transformOrigin: 'center center',
      force3D: true
    });
    
    // Set initial state for hero images container
    if (heroImagesContainerRef.current) {
      // Only set transform origin, don't clear width/height for tablet
      // This allows CSS to control initial dimensions
      gsap.set(heroImagesContainerRef.current, {
        transformOrigin: 'center',
        force3D: true
      });
    }
    
    gsap.set(heroImageRefs.current, { opacity: 0 });
    if (heroImageRefs.current[0]) {
      gsap.set(heroImageRefs.current[0], { opacity: 1 }); // Show first image
    }
    gsap.set(modalRef.current, { opacity: 0, y: 40, pointerEvents: 'none' });

    // Wait until 2 seconds total
    tl.to({}, { duration: 2 });

    // At 2s: Left section fades out and moves down
    tl.to(leftRef.current, {
      opacity: 0,
      y: 15,
      duration: 0.6,
      ease: 'power2.inOut',
      onComplete: () => {
        if (leftRef.current) {
          leftRef.current.style.pointerEvents = 'none';
        }
      }
    });

    // Pause 1 second after left section disappears (at 2.6s)
    tl.to({}, { duration: 1 });

    // At 3.6s: Right section scales to achieve final width
    // Check if we're in tablet viewport (768px - 1024px)
    const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1024;
    const isMobile = window.innerWidth < 768;
    
    // Use CSS classes instead of inline styles
    if (rightSectionRef.current && heroImagesContainerRef.current) {
      if (isTablet) {
        // Tablet version: Smooth transformation from initial wide format to portrait format
        const container = heroImagesContainerRef.current;
        
        // Get current dimensions from CSS state BEFORE any GSAP manipulation
        const currentRect = container.getBoundingClientRect();
        const startWidth = currentRect.width;
        const startHeight = currentRect.height;
        
        // Target portrait dimensions for tablet (500px x 665px)
        const targetWidth = 500;
        const targetHeight = 665;
        
        // Final top position
        const finalTop = 18;
        
        // Remove overflow hidden from .vg-right BEFORE animation starts to prevent clipping
        if (rightSectionRef.current) {
          gsap.set(rightSectionRef.current, {
            overflow: 'visible'
          });
        }
        
        // Single smooth animation using fromTo - explicit start and end to avoid intermediate compression
        // Center the container horizontally using xPercent: -50 and left: 50%
        tl.fromTo(container,
          {
            // Explicit start state
            width: startWidth,
            height: startHeight,
            top: 0,
            left: '50%',
            xPercent: -50,
            yPercent: 0,
            scale: 1,
            transformOrigin: 'center'
          },
          {
            // Direct end state - portrait format centered horizontally
            width: targetWidth,
            height: targetHeight,
            top: finalTop,
            left: '50%',
            xPercent: -50,
            yPercent: 0,
            scale: 0.8,
            transformOrigin: 'center',
            duration: 1.5,
            ease: 'power2.inOut',
            force3D: true,
            immediateRender: false,
            onStart: () => {
              // Expand .vg-right at the start of animation to prevent clipping
              if (rightSectionRef.current) {
                gsap.set(rightSectionRef.current, {
                  height: 'auto',
                  minHeight: '100vh',
                  overflow: 'visible'
                });
              }
            },
            onComplete: () => {
              // Ensure .vg-right is fully expanded after animation completes
              if (rightSectionRef.current) {
                gsap.set(rightSectionRef.current, {
                  height: 'auto',
                  flex: '1 1 auto',
                  minHeight: '100vh',
                  overflow: 'visible'
                });
              }
              // Add classes
              if (container) {
                container.classList.add('vg-hero-images-container--animated');
                container.classList.add('vg-hero-images-container--tablet');
              }
            }
          }
        );
      } else {
        // Desktop/Mobile version
        const container = heroImagesContainerRef.current;
        
        // Get current dimensions from CSS state BEFORE any GSAP manipulation
        const currentRect = container.getBoundingClientRect();
        const startWidth = currentRect.width;
        const startHeight = currentRect.height;
        
        // Target dimensions
        const targetWidth = 640;
        const targetHeight = 840;
        
        // Final top position - same as tablet for desktop, different for mobile
        const finalTop = isMobile ? '55%' : 18;
        
        // Remove overflow hidden from .vg-right BEFORE animation starts to prevent clipping
        if (rightSectionRef.current) {
          gsap.set(rightSectionRef.current, {
            overflow: 'visible'
          });
        }
        
        if (isMobile) {
          // Mobile version: Use percentage-based positioning
          tl.to(container, {
            width: targetWidth,
            height: targetHeight,
            scale: 0.8,
            transformOrigin: 'center',
            xPercent: -50,
            yPercent: -50,
            top: finalTop,
            left: '50%',
            duration: 1.2,
            ease: 'power3.out',
            immediateRender: false,
            onComplete: () => {
              // Add CSS class and clear inline styles so CSS takes over
              if (container) {
                container.classList.add('vg-hero-images-container--animated');
                // Clear GSAP inline styles - CSS class will handle all styling
                gsap.set(container, { 
                  clearProps: 'width,height,top,left,transform,transformOrigin,xPercent,yPercent'
                });
              }
            }
          });
        } else {
          // Desktop version: Same logic as tablet with pixel-based positioning
          tl.fromTo(container,
            {
              // Explicit start state
              width: startWidth,
              height: startHeight,
              top: 0,
              left: '50%',
              xPercent: -50,
              yPercent: 0,
              scale: 1,
              transformOrigin: 'center'
            },
            {
              // Direct end state - portrait format centered horizontally
              width: targetWidth,
              height: targetHeight,
              top: finalTop,
              left: '50%',
              xPercent: -50,
              yPercent: 0,
              scale: 0.8,
              transformOrigin: 'center',
              duration: 1.5,
              ease: 'power2.inOut',
              force3D: true,
              immediateRender: false,
              onStart: () => {
                // Expand .vg-right at the start of animation to prevent clipping
                if (rightSectionRef.current) {
                  gsap.set(rightSectionRef.current, {
                    height: 'auto',
                    minHeight: '100vh',
                    overflow: 'visible'
                  });
                }
              },
              onComplete: () => {
                // Ensure .vg-right is fully expanded after animation completes
                if (rightSectionRef.current) {
                  gsap.set(rightSectionRef.current, {
                    height: 'auto',
                    flex: '1 1 auto',
                    minHeight: '100vh',
                    overflow: 'visible'
                  });
                }
                // Add classes
                if (container) {
                  container.classList.add('vg-hero-images-container--animated');
                  container.classList.add('vg-hero-images-container--desktop');
                }
              }
            }
          );
        }
      }
      
      // Ensure rightSection scale stays at 1 to avoid double scaling
      gsap.set(rightSectionRef.current, { scale: 1 });
    }

    // Pause 1 second after scale animation completes (at 4.8s)
    tl.to({}, { duration: 1 });

    // At 5.8s: Modal appears from bottom left with fade in
    tl.to(modalRef.current, {
      opacity: 1,
      y: 0,
      pointerEvents: 'auto',
      duration: 0.6,
      ease: 'power2.out',
      onComplete: () => {
        if (onModalShow) onModalShow();
      }
    });

    // Continuous hero image fade cycle (starts after modal appears)
    const startHeroCycle = () => {
      heroCycleIntervalRef.current = setInterval(() => {
        setCurrentHeroIndex((prevIndex) => {
          const nextIndex = (prevIndex + 1) % heroImages.length;
          
          // Instant change - no transition
          // Hide current image
          if (heroImageRefs.current[prevIndex]) {
            gsap.set(heroImageRefs.current[prevIndex], {
              opacity: 0
            });
          }
          
          // Show next image
          if (heroImageRefs.current[nextIndex]) {
            gsap.set(heroImageRefs.current[nextIndex], {
              opacity: 1
            });
          }
          
          return nextIndex;
        });
      }, 4000); // Change every 4 seconds
    };

    // Start hero cycle after modal appears with additional delay
    // Modal appears at 5.8s + 0.6s = 6.4s, then add 0.3s delay = 6.7s
    const heroCycleTimeout = setTimeout(() => {
      startHeroCycle();
    }, 6700);

    return () => {
      tl.kill();
      if (heroCycleIntervalRef.current) {
        clearInterval(heroCycleIntervalRef.current);
      }
      clearTimeout(heroCycleTimeout);
    };
  }, []);

  const [initials, setInitials] = useState('');
  
  const handleInputChange = (e) => {
    const value = e.target.value;
    // Only allow alphabetic characters (A-Z), filter out numbers and symbols
    const alphabeticOnly = value.replace(/[^A-Za-z]/g, '').toUpperCase();
    setInitials(alphabeticOnly);
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (initials.trim() && onComplete) {
      trackSubmitLetters(initials.trim().toUpperCase());
      gsap.to(landingRef.current, {
        opacity: 0,
        duration: 0.5,
        ease: 'power2.inOut',
        onComplete: () => {
          onComplete(initials.trim().toUpperCase());
        }
      });
    }
  };

  return (
    <div className="vg-landing" ref={landingRef}>
      <div className="vg-left" ref={leftRef}>
        <div className="vg-brand-center">
          <img ref={brandLogoRef} src={garValenLogo} alt="Valentino Garavani" className="vg-brand-logo" draggable={false} />
          <div ref={presentsRef} className="vg-presents">{t('PRESENTS')}</div>
          <div ref={devainRef} className="vg-devain">{t('DEVAIN_BAG')}</div>
        </div>
      </div>
      <div className="vg-right" ref={rightSectionRef}>
        <div className="vg-hero-images-container" ref={heroImagesContainerRef}>
          {heroImages.map((src, i) => (
            <img
              key={i}
              ref={el => (heroImageRefs.current[i] = el)}
              src={src}
              alt={`Hero ${i + 1}`}
              className="vg-hero-image"
              draggable={false}
            />
          ))}
        </div>
      </div>
      <div ref={modalRef} className="vg-center-modal">
        <div className="vg-center-modal-box">
          <div className="vg-modal-title">{t('WELCOME_PERSONALIZATION')}</div>
          <div className="vg-modal-sub">{t('BEGIN_MAKING_UNIQUE')}</div>
          <form className="vg-modal-form" onSubmit={handleSubmit}>
            <input type="text" maxLength={3} placeholder="AAA" value={initials} onChange={handleInputChange} className="vg-modal-input" onFocus={() => setTimeout(() => window.scrollTo(0, 0), 50)} />
            <button type="submit" className="vg-modal-btn" onClick={() => window.scrollTo(0, 0)}>
              <img src={arrowIcon} alt="Submit" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoaderLanding;
